import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { ResponsiveBar } from '@nivo/bar';
import styled from '@emotion/styled';
import {
  Box,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { useOrdinalColorScale } from '@nivo/colors';

import { Retailer, useSpot, FunnelData } from 'framework';
import { Row, Gap } from 'components';

const ChartContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 500px;
  width: 100%;
  margin: auto;
  padding: ${p => p.theme.spacing(4)};
  margin-bottom: 40px;
  position: relative;

  ${p => p.theme.breakpoints.down('sm')} {
    margin: ${p => p.theme.spacing(1)};
    padding: ${p => p.theme.spacing(1)};
  }
`;

const LoadingContainer = styled(Box)`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const ControlsContainer = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing(3)};
  width: 100%;
  max-width: 300px;
`;

interface FunnelExplorerProps {
  startDate: moment.Moment;
  endDate: moment.Moment;
  retailer: Retailer | null;
}

type FunnelType = 'try-on-flow' | 'size-me-flow';

export function FunnelExplorer({
  startDate,
  endDate,
  retailer
}: FunnelExplorerProps) {
  const { t } = useTranslation();
  const { spot, query } = useSpot();
  const [loading, setLoading] = useState(false);
  const [funnelType, setFunnelType] = useState<FunnelType>('try-on-flow');

  const colorScale = useOrdinalColorScale({ scheme: 'category10' }, 'step');

  const fetchFunnelData = useCallback(async () => {
    if (!retailer && spot.data?.profile?.role !== 'admin') {
      return;
    }

    const request: {
      startDate: string;
      endDate: string;
      retailers?: string;
    } = {
      startDate: moment(startDate).startOf('day').toISOString(),
      endDate: moment(endDate).endOf('day').toISOString()
    };

    if (retailer) {
      request.retailers = `${encodeURIComponent(retailer.name)},${retailer.slug}`;
    } else if (spot.data?.profile?.role === 'admin') {
      // For admin users without a specific retailer, leave retailers undefined for 'all'
      // request.retailers is optional according to the API spec
    }

    setLoading(true);
    try {
      await query(`analytics/funnel/${funnelType}`, request, [
        'analytics',
        'funnel',
        funnelType === 'try-on-flow' ? 'tryOnFlow' : 'sizeMeFlow'
      ]);
    } catch (error) {
      console.error('Error fetching funnel data:', error);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, retailer, funnelType, spot, query]);

  useEffect(() => {
    fetchFunnelData();
  }, [fetchFunnelData]);

  const handleFunnelTypeChange = useCallback((event: SelectChangeEvent) => {
    setFunnelType(event.target.value as FunnelType);
  }, []);

  // Get the funnel data from spot.data based on the selected funnel type
  const funnelData =
    funnelType === 'try-on-flow'
      ? spot.data.analytics?.funnel?.tryOnFlow?.funnel || []
      : spot.data.analytics?.funnel?.sizeMeFlow?.funnel || [];

  // Transform data for the bar chart - use users as the main metric
  const chartData = funnelData.map((item, index) => ({
    step: item.step,
    users: item.users,
    conversions: item.conversions,
    conversionRate: item.conversionRate,
    color: colorScale({ step: item.step })
  }));

  if (!retailer && spot.data?.profile?.role !== 'admin') {
    return <Typography>{t('funnelExplorerSelectRetailer')}</Typography>;
  }

  return (
    <>
      <Row>
        <ControlsContainer>
          <FormControl fullWidth>
            <InputLabel id="funnel-type-label">{t('funnelType')}</InputLabel>
            <Select
              labelId="funnel-type-label"
              label={t('funnelType')}
              value={funnelType}
              onChange={handleFunnelTypeChange}
            >
              <MenuItem value="try-on-flow">{t('tryOnFlow')}</MenuItem>
              <MenuItem value="size-me-flow">{t('sizeMeFlow')}</MenuItem>
            </Select>
          </FormControl>
        </ControlsContainer>
      </Row>
      <Gap />

      <ChartContainer>
        <Typography variant="h6" gutterBottom>
          {t('funnelVisualization')} -{' '}
          {t(funnelType === 'try-on-flow' ? 'tryOnFlow' : 'sizeMeFlow')}
        </Typography>

        {loading && (
          <LoadingContainer>
            <CircularProgress />
          </LoadingContainer>
        )}

        {!loading && chartData.length > 0 && (
          <ResponsiveBar
            data={chartData}
            keys={['users']}
            indexBy="step"
            margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
            padding={0.3}
            layout="vertical"
            reverse={false}
            colors={({ data }) => data.color}
            borderColor={{
              from: 'color',
              modifiers: [['darker', 1.6]]
            }}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: t('users'),
              legendPosition: 'middle',
              legendOffset: 32
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: t('funnelSteps'),
              legendPosition: 'middle',
              legendOffset: -40
            }}
            labelSkipWidth={12}
            labelSkipHeight={12}
            labelTextColor={{
              from: 'color',
              modifiers: [['darker', 1.6]]
            }}
            tooltip={({ data }) => (
              <div
                style={{
                  background: 'white',
                  padding: '12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
              >
                <strong>{data.step}</strong>
                <br />
                {t('users')}: {data.users.toLocaleString()}
                <br />
                {t('conversions')}: {data.conversions.toLocaleString()}
                <br />
                {t('conversionRate')}: {(data.conversionRate * 100).toFixed(2)}%
              </div>
            )}
            animate={true}
          />
        )}

        {!loading && chartData.length === 0 && (
          <Typography color="textSecondary">
            {t('noFunnelDataAvailable')}
          </Typography>
        )}
      </ChartContainer>
    </>
  );
}
